import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { getApiBase } from '../utils/apiBase';

// Single sign-on: the login-page lookup and the tenant-admin configuration.

interface DataEnvelope<T> {
  data: T;
}

export type SSOProviderKind = 'oidc' | 'google_shared';

// ── login page ───────────────────────────────────────────────────────────────

export interface AuthLookupResult {
  mode: 'password' | 'sso';
  kind: SSOProviderKind | null;
  provider_name: string | null;
  /** API-relative, e.g. /api/auth/sso/start?slug=acme */
  start_url: string | null;
  /** Two organisations claim this email domain; the user must use their org's link. */
  ambiguous: boolean;
}

export interface SSOEntry {
  mode: 'sso';
  kind: SSOProviderKind;
  provider_name: string;
  start_url: string;
}

/** Start URL for the always-visible "Sign in with Google" button: Google
 *  reports the account's domain and the API finds the organisation from it. */
export const GOOGLE_START_URL = '/api/auth/sso/start?google=1';

export const lookupAuth = async (email: string): Promise<AuthLookupResult> => {
  const res = await api.request<DataEnvelope<AuthLookupResult>>('/api/auth/lookup', {
    method: 'POST',
    body: { email },
  });
  return res.data;
};

export const getSsoEntry = async (slug: string): Promise<SSOEntry | null> => {
  try {
    const res = await api.request<DataEnvelope<SSOEntry>>(
      `/api/auth/sso/${encodeURIComponent(slug)}`
    );
    return res.data;
  } catch {
    return null;
  }
};

/**
 * Full-page navigation to the API's SSO start URL. The callback sets the
 * session cookie and redirects back to this app at `next`.
 */
export const startSso = (startUrl: string, opts: { email?: string; next?: string | null } = {}) => {
  const params = new URLSearchParams();
  if (opts.email) params.set('login_hint', opts.email);
  if (opts.next && opts.next.startsWith('/') && !opts.next.startsWith('//')) {
    params.set('next', opts.next);
  }
  const sep = startUrl.includes('?') ? '&' : '?';
  const query = params.toString();
  window.location.href = `${getApiBase()}${startUrl}${query ? sep + query : ''}`;
};

/** Copy for the `sso_error` codes the API redirects back with. */
export const SSO_ERROR_MESSAGES: Record<string, string> = {
  no_account:
    "Your sign-in worked, but there's no Mitigate account for you yet. Ask your Mitigate administrator to add you.",
  account_disabled: 'Your Mitigate account is deactivated. Ask your administrator.',
  domain_not_allowed: "That account's email domain isn't allowed for your organisation.",
  email_unverified: "Your Google account's email address isn't verified.",
  expired: 'The sign-in took too long or was started in another window. Try again.',
  state_mismatch: "The sign-in couldn't be verified. Try again.",
  idp_error: "Your organisation's sign-in page reported a problem.",
  token_exchange_failed: "We couldn't complete sign-in with your organisation.",
  invalid_token: "We couldn't verify the sign-in from your organisation.",
  provider_disabled: 'Single sign-on is switched off for your organisation.',
  unknown_org: "We couldn't find single sign-on for that organisation.",
  no_sso_for_domain: "Single sign-on isn't set up for that email address.",
  google_not_enabled:
    "Sign in with Google isn't enabled for that Google account's organisation. Sign in with your username and password, or ask your Mitigate administrator.",
  config_error: "Single sign-on for your organisation isn't configured correctly.",
};

// ── admin ────────────────────────────────────────────────────────────────────

export interface SSOProvider {
  id: number;
  provider_kind: SSOProviderKind;
  display_name: string;
  enabled: boolean;
  discovery_url: string;
  issuer: string;
  client_id: string;
  has_client_secret: boolean;
  client_secret_hint: string;
  allowed_email_domains: string[];
  jit_enabled: boolean;
  jit_default_role_id: number | null;
  enforce_sso: boolean;
  sso_session_hours: number;
  linked_users: number;
  updated_at: string;
}

export interface SSOSettings {
  configured: boolean;
  tenant_slug: string | null;
  redirect_uri: string;
  shared_google_available: boolean;
  provider: SSOProvider | null;
}

export interface SSOProviderInput {
  provider_kind: SSOProviderKind;
  display_name: string;
  enabled: boolean;
  discovery_url: string;
  client_id: string;
  /** Omit or null to keep the stored secret. */
  client_secret?: string | null;
  allowed_email_domains: string[];
  jit_enabled: boolean;
  jit_default_role_id: number | null;
  enforce_sso: boolean;
  sso_session_hours: number;
}

export interface SSOTestResult {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  jwks_uri: string;
  jwks_keys: number;
  code_challenge_methods_supported: string[];
  scopes_supported: string[];
  warnings: string[];
}

export const useSSOSetting = (enabled = true) =>
  useQuery({
    queryKey: ['sso-setting'],
    enabled,
    queryFn: async () => {
      const res = await api.request<DataEnvelope<SSOSettings>>('/api/admin/tenant/sso');
      return res.data;
    },
  });

export const useUpdateSSOSetting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SSOProviderInput) => {
      const res = await api.request<DataEnvelope<SSOSettings>>('/api/admin/tenant/sso', {
        method: 'PUT',
        body,
      });
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['sso-setting'], data);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useDeleteSSOSetting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.request<DataEnvelope<{ deleted: boolean; identities_removed: number }>>(
        '/api/admin/tenant/sso',
        { method: 'DELETE' }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sso-setting'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useTestSSO = () =>
  useMutation({
    mutationFn: async (body: { provider_kind: SSOProviderKind; discovery_url?: string }) => {
      const res = await api.request<DataEnvelope<SSOTestResult>>('/api/admin/tenant/sso/test', {
        method: 'POST',
        body,
      });
      return res.data;
    },
  });
